import { useState } from 'react';
import { useApolloClient } from '@apollo/client';
import {
    ClaimBriefDocument,
    ClaimBriefMutation,
    ClaimBriefMutationVariables,
    LaunchBriefDocument,
    LaunchBriefMutation,
    LaunchBriefMutationVariables,
} from '@99designs/apps/spa/pages/create/briefs/Brief.generated';
import { BriefState } from '@99designs/graph-utils/types';
import Bugsnag from '@bugsnag/js';

export enum LaunchState {
    InProgress = 'InProgress',
    Success = 'Success',
    Error = 'Error',
}

interface Brief {
    id: string;
    state: BriefState;
    userId: number;
}
type LaunchFn = (options: { delay: number }) => Promise<void>;
type ClaimFn = () => Promise<void>;

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

function useWorkflowMutations(
    brief: Brief
): [LaunchState | null, LaunchFn, ClaimFn, LaunchBriefMutation | null] {
    const client = useApolloClient();
    const [launchState, setLaunchState] = useState<LaunchState | null>(null);
    const [data, setData] = useState<LaunchBriefMutation | null>(null);

    async function claim() {
        if (brief.userId) {
            return;
        }

        await client.mutate<ClaimBriefMutation, ClaimBriefMutationVariables>({
            mutation: ClaimBriefDocument,
            variables: {
                id: brief.id,
            },
        });
    }

    /**
     * Launch brief (with optional artifical delay)
     */
    async function launch({ delay }: { delay: number }) {
        // eslint-disable-next-line eqeqeq
        if (brief.state == 'LAUNCHED') {
            return;
        }

        let launchTimeout;

        try {
            setLaunchState(LaunchState.InProgress);

            // We've had some reports of launches stalling without an error. This is currently
            // here to give us a picture of how many users are being affected by this.
            launchTimeout = setTimeout(
                () =>
                    Bugsnag.notify(new Error('Launch timeout'), (event) => {
                        event.addMetadata('brief', 'id', brief.id);
                    }),
                15000
            );

            const res = await client.mutate<LaunchBriefMutation, LaunchBriefMutationVariables>({
                mutation: LaunchBriefDocument,
                variables: { id: brief.id },
            });

            if (res.data) {
                await sleep(delay);
                setData(res.data);
                setLaunchState(LaunchState.Success);
            }
        } catch (error) {
            Bugsnag.notify(error, (event) => {
                event.context = 'Launch error';
                event.addMetadata('brief', 'id', brief.id);
            });
            setLaunchState(LaunchState.Error);
        } finally {
            if (launchTimeout) {
                clearTimeout(launchTimeout);
            }
        }
    }

    return [launchState, launch, claim, data];
}

export default useWorkflowMutations;
